import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, X, Loader2, Film, Play, Image, Upload, Video, FileVideo } from "lucide-react";
import { getImageUrl } from "@/lib/imageUtils";

const MediaManager = () => {
  const { authAxios } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    media_type: "video_highlight",
    url: "",
    thumbnail_url: "",
    description: ""
  });

  const mediaTypes = [
    { value: "video_highlight", label: "Video Highlight" },
    { value: "drill_video", label: "Drill Video" },
    { value: "photo", label: "Photo" }
  ];

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data);
    } catch (e) {
      toast.error("Failed to fetch media");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video file too large. Max size is 100MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'media_videos');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      // Construct full URL
      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, url: fullUrl });
      toast.success("Video uploaded successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload video");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'thumbnails');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, thumbnail_url: fullUrl });
      toast.success("Thumbnail uploaded!");
    } catch (e) {
      toast.error("Failed to upload thumbnail");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'media_photos');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, url: fullUrl, thumbnail_url: fullUrl });
      toast.success("Photo uploaded!");
    } catch (e) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAxios.post('/media', formData);
      toast.success("Media added successfully!");
      setShowForm(false);
      resetForm();
      fetchMedia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add media");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;
    
    try {
      await authAxios.delete(`/media/${id}`);
      toast.success("Media deleted");
      fetchMedia();
    } catch (e) {
      toast.error("Failed to delete media");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      media_type: "video_highlight",
      url: "",
      thumbnail_url: "",
      description: ""
    });
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'video_highlight': return <Play className="w-4 h-4 text-neon-pink" />;
      case 'drill_video': return <Film className="w-4 h-4 text-neon-blue" />;
      default: return <Image className="w-4 h-4 text-neon-green" />;
    }
  };

  const isVideoType = formData.media_type === 'video_highlight' || formData.media_type === 'drill_video';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-pink animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Film className="w-5 h-5 text-neon-pink" />
          Media Library ({media.length})
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-media-btn"
          className="cyber-btn cyber-btn-pink py-2 px-4 text-xs"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD MEDIA"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-neon-pink/30" data-testid="media-form">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                placeholder="Media title"
                className="bg-void-black border-white/10"
                data-testid="media-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Type *</Label>
              <Select value={formData.media_type} onValueChange={(v) => setFormData({...formData, media_type: v, url: '', thumbnail_url: ''})}>
                <SelectTrigger className="bg-void-black border-white/10" data-testid="media-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10">
                  {mediaTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Upload Section - Conditional based on type */}
          {isVideoType ? (
            // Video Upload Section
            <div className="border border-dashed border-neon-pink/50 p-4 space-y-4">
              <Label className="font-orbitron text-sm text-neon-pink flex items-center gap-2">
                <Video className="w-5 h-5" />
                VIDEO UPLOAD
              </Label>
              
              <div className="flex flex-col md:flex-row gap-4">
                {/* Video Preview/Upload Area */}
                <div className="flex-1">
                  <div className="aspect-video bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden relative">
                    {formData.url && !formData.url.includes('youtube') ? (
                      <video 
                        src={formData.url} 
                        controls 
                        className="w-full h-full object-contain"
                      />
                    ) : formData.url && formData.url.includes('youtube') ? (
                      <iframe
                        src={formData.url}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : (
                      <div className="text-center p-6">
                        <FileVideo className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                        <p className="font-rajdhani text-sm text-gray-500">No video selected</p>
                      </div>
                    )}
                    
                    {uploading && (
                      <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                        <Loader2 className="w-8 h-8 text-neon-pink animate-spin mb-2" />
                        <p className="font-rajdhani text-sm text-white">Uploading... {uploadProgress}%</p>
                        <div className="w-48 h-2 bg-white/20 mt-2 overflow-hidden">
                          <div 
                            className="h-full bg-neon-pink transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Controls */}
                <div className="md:w-64 space-y-3">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/mov,video/avi"
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                  />
                  <label
                    htmlFor="video-upload"
                    className={`cyber-btn cyber-btn-pink py-3 px-4 text-xs cursor-pointer w-full flex items-center justify-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid="video-upload-btn"
                  >
                    <Upload className="w-4 h-4" />
                    UPLOAD VIDEO FILE
                  </label>
                  
                  <p className="font-rajdhani text-xs text-gray-500 text-center">
                    MP4, WebM, MOV, AVI up to 100MB
                  </p>
                  
                  <div className="border-t border-white/10 pt-3">
                    <p className="font-rajdhani text-xs text-gray-400 text-center mb-2">
                      OR paste YouTube URL
                    </p>
                    <Input
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value})}
                      placeholder="https://www.youtube.com/embed/..."
                      className="bg-void-black border-white/10 text-sm"
                      data-testid="media-url-input"
                    />
                  </div>
                </div>
              </div>

              {/* Thumbnail Upload */}
              <div className="border-t border-white/10 pt-4">
                <Label className="font-orbitron text-xs text-gray-400 mb-2 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  THUMBNAIL (Optional)
                </Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-14 bg-white/5 border border-white/10 overflow-hidden flex-shrink-0">
                    {formData.thumbnail_url ? (
                      <img src={formData.thumbnail_url} alt="Thumbnail" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex gap-2">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <label
                      htmlFor="thumbnail-upload"
                      className="cyber-btn py-2 px-3 text-xs cursor-pointer"
                    >
                      <Upload className="w-3 h-3" />
                    </label>
                    <Input
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})}
                      placeholder="Or paste thumbnail URL..."
                      className="bg-void-black border-white/10 text-sm flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Photo Upload Section
            <div className="border border-dashed border-neon-green/50 p-4 space-y-4">
              <Label className="font-orbitron text-sm text-neon-green flex items-center gap-2">
                <Image className="w-5 h-5" />
                PHOTO UPLOAD
              </Label>
              
              <div className="flex items-start gap-4">
                {/* Image Preview */}
                <div className="w-40 h-40 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {formData.url ? (
                    <img src={formData.url} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Image className="w-10 h-10 text-gray-600 mx-auto mb-1" />
                      <p className="font-rajdhani text-xs text-gray-500">No image</p>
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`cyber-btn cyber-btn-green py-3 px-4 text-xs cursor-pointer inline-flex items-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid="photo-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        UPLOADING...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        UPLOAD PHOTO
                      </>
                    )}
                  </label>
                  
                  <p className="font-rajdhani text-xs text-gray-500">
                    JPG, PNG, GIF, WebP up to 5MB
                  </p>
                  
                  <div className="border-t border-white/10 pt-3">
                    <p className="font-rajdhani text-xs text-gray-400 mb-2">
                      Or paste image URL
                    </p>
                    <Input
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value, thumbnail_url: e.target.value})}
                      placeholder="https://..."
                      className="bg-void-black border-white/10 text-sm"
                      data-testid="media-url-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description..."
              rows={2}
              className="bg-void-black border-white/10 font-rajdhani"
              data-testid="media-description-input"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn cyber-btn-pink py-2 px-6 text-xs" data-testid="save-media-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE MEDIA
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {media.map(item => (
          <div key={item.id} className="cyber-card overflow-hidden group" data-testid={`media-item-${item.id}`}>
            <div className="aspect-video bg-white/5 relative">
              {(item.media_type === 'video_highlight' || item.media_type === 'drill_video') && item.url && !item.url.includes('youtube') ? (
                <video 
                  src={getImageUrl(item.url)}
                  className="w-full h-full object-cover"
                  muted
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
              ) : (
                <img 
                  src={getImageUrl(item.thumbnail_url || item.url)} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/400x225?text=No+Image'}
                />
              )}
              <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 flex items-center gap-1">
                {getTypeIcon(item.media_type)}
                <span className="font-rajdhani text-xs text-white capitalize">
                  {item.media_type.replace('_', ' ')}
                </span>
              </div>
            </div>
            <div className="p-3">
              <h3 className="font-orbitron text-xs text-white truncate">{item.title}</h3>
              {item.description && (
                <p className="font-rajdhani text-xs text-gray-500 truncate mt-1">{item.description}</p>
              )}
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-gray-500 hover:text-red-500 transition-colors p-1"
                  data-testid={`delete-media-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {media.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-media-state">
          <Film className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No media yet. Add videos and photos!</p>
        </div>
      )}
    </div>
  );
};

export default MediaManager;
